import React, { useEffect, useState } from "react"
import Loader from "components/Loader"
import DatePicker from "react-datepicker"
import moment from "moment"
import { useSelector } from "react-redux"
import {
  useQueryParam,
  StringParam,
  withDefault
} from "use-query-params"
import { getBaseUrl } from "../../reducers"
import { getTimeSpentDraftingReports } from "../../../../requesters/timeSpentRequester"
import { getTeamsBlockOutList } from "../../../../requesters/userRequester"
import FilterDropdown from "components/FilterDropdown"
import ReportTable from "./DraftingReport/ReportTable"
import { DRAFTING_TYPE_OPTIONS, DRAFTING_TEAM_SLUGS_FOR_LEAVE } from "utils/constants"

const getDate = val => {
  const momentObj = moment(val)
  return momentObj.format("YYYY-MM-DD")
}

const defaultStartDate = () => getDate(moment().startOf("month"))
const defaultEndDate = () => getDate(new Date())

const customStartDateParam = withDefault(StringParam, defaultStartDate())
const customEndDateParam = withDefault(StringParam, defaultEndDate())

const DraftingOverview = () => {
  const baseUrl = useSelector(getBaseUrl)

  const [requesting, setRequesting] = useState(false)
  const [byDraftingType, setByDraftingType] = useState([])
  const [total, setTotal] = useState({ jobsComplete: 0, totalWorkedTime: 0 })
  const [leaveDetails, setLeaveDetails] = useState([])
  const [startDate, setStartDate] = useQueryParam("drafting_start_date", customStartDateParam)
  const [endDate, setEndDate] = useQueryParam("drafting_end_date", customEndDateParam)
  const defaultDraftingTypeParam = withDefault(StringParam, "all")
  const [draftingType, setDraftingType] = useQueryParam("drafting_type", defaultDraftingTypeParam)

  const filters = {
    start_date: startDate,
    end_date: endDate,
    drafting_type: draftingType || "all"
  }

  const fetchData = () => {
    setRequesting(true)
    const startAt = moment(startDate).startOf("day").toISOString()
    const endAt = moment(endDate).endOf("day").toISOString()
    Promise.all([
      getTimeSpentDraftingReports(baseUrl, filters),
      getTeamsBlockOutList(baseUrl, {
        by_teams: DRAFTING_TEAM_SLUGS_FOR_LEAVE,
        start_at: startAt,
        end_at: endAt
      })
    ])
      .then(([reportRes, leaveRes]) => {
        if (!reportRes.error && reportRes.status === 200) {
          const raw = reportRes.response ?? reportRes.data
          const data = raw?.json ?? raw
          if (data) {
            setByDraftingType(data.by_drafting_type || [])
            setTotal(data.total || { jobsComplete: 0, totalWorkedTime: 0 })
          }
        }
        if (!leaveRes.error && leaveRes.status === 200 && leaveRes.response) {
          const userBlockOut = leaveRes.response.userBlockOut || {}
          const list = Array.isArray(userBlockOut) ? userBlockOut : Object.values(userBlockOut)
          setLeaveDetails(
            list.map(bo => ({
              userName: bo.attributes?.userName ?? bo.attributes?.user_name,
              leaveType: bo.attributes?.leaveType ?? bo.attributes?.leave_type,
              startAt: bo.attributes?.startAt ?? bo.attributes?.start_at,
              endAt: bo.attributes?.endAt ?? bo.attributes?.end_at
            }))
          )
        } else {
          setLeaveDetails([])
        }
      })
      .catch(err => console.log(err))
      .finally(() => setRequesting(false))
  }

  const onStartDateChange = val => {
    setStartDate(getDate(val))
  }

  const onEndDateChange = val => {
    setEndDate(getDate(val))
  }

  const onSelectDraftingType = val => {
    setDraftingType(val)
  }

  useEffect(() => {
    fetchData()
  }, [startDate, endDate, draftingType])

  const clearStates = () => {
    setStartDate(undefined)
    setEndDate(undefined)
    setDraftingType(undefined)
    setByDraftingType([])
    setTotal({ jobsComplete: 0, totalWorkedTime: 0 })
    setLeaveDetails([])
  }

  useEffect(() => () => clearStates(), [])

  const startDateObject = moment(startDate, "YYYY-MM-DD").toDate()
  const endDateObject = moment(endDate, "YYYY-MM-DD").toDate()

  const draftingTypeLabel = DRAFTING_TYPE_OPTIONS.find(opt => opt[1] === draftingType)?.[0] || "All"

  return (
    <React.Fragment>
      <div className="container-inner">
        <div className="table-listing-filters d-md-flex flex-row align-items-center">
          <div className="btn-group mr-2">
            <div className="ca-custom">
              <DatePicker
                selected={startDateObject}
                selectsStart
                startDate={startDateObject}
                endDate={endDateObject}
                dateFormat="dd/MM/yyyy"
                onChange={onStartDateChange}
                placeholderText="From"
                closeOnScroll={e => e.target === document}
                className="form-control" />
            </div>
            <div className="ca-custom">
              <DatePicker
                selected={endDateObject}
                selectsEnd
                startDate={startDateObject}
                endDate={endDateObject}
                dateFormat="dd/MM/yyyy"
                onChange={onEndDateChange}
                placeholderText="To"
                closeOnScroll={e => e.target === document}
                className="form-control" />
            </div>
          </div>
          <FilterDropdown
            title="Drafting Type"
            filteredName={draftingTypeLabel}
            setFilter={onSelectDraftingType}
            options={DRAFTING_TYPE_OPTIONS} />
        </div>
      </div>
      {requesting ? <Loader /> : <ReportTable byDraftingType={byDraftingType} total={total} leaveDetails={leaveDetails} />}
    </React.Fragment>
  )
}

export default DraftingOverview
